// ---- The chop minigame (tree-cutting) ---------------------------------------
// WASD/arrow lane twin of the fishing reel: prompts fall toward a hit line;
// press the matching key in the window. Progress = prompts cleared; a fail
// meter rises on misses. Stamina is shown as a hint — spend is owned by the
// activity/server, not this module.
//
// Import discipline: sim/chopping.ts + types.ts only — NO game/net/state imports.

import {
  startChopJudge, applyChopResult,
  type ChopPattern, type ChopTuning, type ChopJudge,
} from "../sim/chopping.ts";
import type { Minigame } from "./types.ts";

const LANE_KEYS = ["w", "a", "s", "d"] as const;
const LANE_GLYPHS = ["▲", "◀", "▼", "▶"] as const;
const ALIAS: Record<string, number> = {
  w: 0, a: 1, s: 2, d: 3,
  ArrowUp: 0, ArrowLeft: 1, ArrowDown: 2, ArrowRight: 3,
};

const LANE_H = 240;
const PX_PER_SEC = 150;
const HIT_Y = LANE_H - 28;

export type ChopGameEvents = {
  /** `finished` = this input resolved the pattern (landed or escaped). */
  onInput(i: number, hit: boolean, finished: boolean): void;
};

export function makeChopMinigame(cfg: {
  pattern: ChopPattern;
  hitWindow: number;
  tun: Pick<ChopTuning, "missTension" | "hitRelief">;
  ev: ChopGameEvents;
  title?: string;
  /** Optional live stamina readout (activity updates via setStamina). */
  staminaLabel?: () => string;
}): Minigame & { setStaminaLabel(s: string): void } {
  const { pattern, hitWindow, tun, ev } = cfg;
  const title = cfg.title ?? "🪓 CHOP — match the keys!";
  let root: HTMLElement | null = null;
  let tensionFill: HTMLElement | null = null;
  let progressEl: HTMLElement | null = null;
  let staEl: HTMLElement | null = null;
  let arrowEls: HTMLElement[] = [];
  let laneEls: HTMLElement[] = [];
  let judge: ChopJudge = startChopJudge(pattern, hitWindow);
  let elapsed = 0;
  let done = false;
  let staText = cfg.staminaLabel?.() ?? "";

  const flashLane = (lane: number, ok: boolean) => {
    const el = laneEls[lane];
    if (!el) return;
    el.style.background = ok ? "rgba(120,220,140,0.28)" : "rgba(230,90,80,0.28)";
    setTimeout(() => { el.style.background = "rgba(10,18,26,0.55)"; }, 120);
  };

  const judgeNext = (hit: boolean) => {
    if (done || !root) return;
    const i = judge.idx;
    const evs = applyChopResult(judge, hit, tun);
    const el = arrowEls[i];
    if (el) {
      el.style.opacity = "0.25";
      el.style.color = hit ? "#7ee29a" : "#e66a5a";
    }
    const finished = evs.length > 0;
    if (finished) done = true;
    // Activity may fully unmount mid-callback (e.g. out of stamina). Do not
    // touch DOM after this returns if root was cleared.
    ev.onInput(i, hit, finished);
  };

  return {
    setStaminaLabel(s: string) {
      staText = s;
      if (staEl) staEl.textContent = s;
    },

    mount() {
      if (root) return;
      root = document.createElement("div");
      root.id = "minigame";
      root.style.cssText =
        "position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:60;" +
        "display:flex;flex-direction:column;align-items:center;gap:6px;pointer-events:none;" +
        "font-family:inherit;user-select:none;";

      const lanes = document.createElement("div");
      lanes.style.cssText = "display:flex;gap:8px;";
      laneEls = [];
      arrowEls = [];
      for (let lane = 0; lane < 4; lane++) {
        const laneEl = document.createElement("div");
        laneEl.style.cssText =
          `position:relative;width:52px;height:${LANE_H}px;overflow:hidden;border-radius:8px;` +
          "background:rgba(10,18,26,0.55);border:1px solid rgba(255,255,255,0.14);transition:background 80ms;";
        const line = document.createElement("div");
        line.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y}px;height:2px;background:rgba(255,255,255,0.6);`;
        const label = document.createElement("div");
        label.textContent = LANE_KEYS[lane]!.toUpperCase();
        label.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y + 4}px;text-align:center;` +
          "font-size:13px;font-weight:700;color:rgba(255,255,255,0.65);";
        laneEl.append(line, label);
        lanes.appendChild(laneEl);
        laneEls.push(laneEl);
      }
      for (let i = 0; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = document.createElement("div");
        el.textContent = LANE_GLYPHS[a.dir]!;
        el.style.cssText =
          "position:absolute;left:0;right:0;text-align:center;font-size:26px;line-height:1;" +
          "color:#ffd97a;text-shadow:0 1px 3px rgba(0,0,0,0.7);will-change:transform;transform:translateY(-40px);";
        laneEls[a.dir]!.appendChild(el);
        arrowEls.push(el);
      }

      const tension = document.createElement("div");
      tension.style.cssText =
        "width:232px;height:8px;border-radius:4px;background:rgba(10,18,26,0.6);" +
        "border:1px solid rgba(255,255,255,0.14);overflow:hidden;";
      tensionFill = document.createElement("div");
      tensionFill.style.cssText =
        "height:100%;width:0%;background:linear-gradient(90deg,#e6b45a,#e66a5a);transition:width 90ms;";
      tension.appendChild(tensionFill);

      progressEl = document.createElement("div");
      progressEl.style.cssText = "font-size:12px;color:rgba(255,255,255,0.8);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      progressEl.textContent = `chop! 0 / ${pattern.arrows.length}`;

      staEl = document.createElement("div");
      staEl.style.cssText = "font-size:11px;color:rgba(140,220,160,0.9);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      staEl.textContent = staText;

      if (title) {
        const titleEl = document.createElement("div");
        titleEl.textContent = title;
        titleEl.style.cssText =
          "font:bold 15px ui-monospace,monospace;color:#ffd86a;white-space:nowrap;" +
          "background:rgba(0,0,0,0.67);padding:8px 16px;border-radius:20px;box-shadow:0 2px 10px #0006;";
        root.append(titleEl, lanes, tension, progressEl, staEl);
      } else {
        root.append(lanes, tension, progressEl, staEl);
      }
      document.body.appendChild(root);
      judge = startChopJudge(pattern, hitWindow);
      elapsed = 0;
      done = false;
    },

    tick(dt: number) {
      if (!root) return;
      elapsed += dt;
      // Timeouts may call onInput → activity teardown → unmount() mid-loop.
      // Bail as soon as root is cleared so we never touch disposed arrow nodes
      // (that throw killed requestAnimationFrame and froze the whole game).
      while (
        root && !done &&
        judge.idx < pattern.arrows.length &&
        elapsed > pattern.arrows[judge.idx]!.t + hitWindow
      ) {
        flashLane(pattern.arrows[judge.idx]!.dir, false);
        judgeNext(false);
      }
      if (!root) return;
      for (let i = judge.idx; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = arrowEls[i];
        if (!el) continue;
        const y = HIT_Y - (a.t - elapsed) * PX_PER_SEC;
        el.style.transform = `translateY(${Math.round(y - 26)}px)`;
      }
      if (tensionFill) tensionFill.style.width = `${Math.min(100, judge.tension * 100)}%`;
      if (progressEl && !done) progressEl.textContent = `chop! ${judge.idx} / ${pattern.arrows.length}`;
      if (progressEl && done) progressEl.textContent = judge.tension >= 1 ? "too many misses…" : "timber!";
      if (staEl && cfg.staminaLabel) staEl.textContent = cfg.staminaLabel();
    },

    key(e: KeyboardEvent): boolean {
      if (!root || done) return false;
      const dir = ALIAS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (dir === undefined) return false;
      const i = judge.idx;
      const a = pattern.arrows[i];
      if (a && a.dir === dir && Math.abs(elapsed - a.t) <= hitWindow) {
        flashLane(dir, true);
        judgeNext(true);
      } else {
        // Missed swing (wrong key or bad timing): still resolve the prompt as a
        // miss so tension rises AND stamina is spent (via onInput) — same cost
        // as letting the arrow time out. No free practice swings.
        flashLane(dir, false);
        if (a) judgeNext(false);
      }
      return true;
    },

    unmount() {
      root?.remove();
      root = null;
      tensionFill = null;
      progressEl = null;
      staEl = null;
      arrowEls = [];
      laneEls = [];
    },
  };
}
